import { EditorChangeType } from '@src/editor';
import { KARAOKE_TRACK_NUMBER } from '@src/editor/constants';
import { useTimeline } from '@src/hooks/timeline/useTimeline';
import { useAddMediaToTimeline } from '@src/hooks/useAddMediaToTimeline';
import { useKaraoke } from '@src/hooks/useKaraoke';
import {
  addSingleElementType,
  addVideoTemplate,
  computeNamecardSync,
  createNamecardsFromSyncResult,
  getSpeakerSignature,
} from '@src/services/videoTemplate/VideoTemplateService';
import {
  useDatoPerson,
  useKaraokeStore,
  useStore,
  useUserIdentity,
} from '@src/stores-v2';
import { DesignCard } from '@src/types.ts/general';
import { analytics } from '@src/utility/analytics';
import { extractErrorInfo } from '@src/utility/error';
import {
  deleteElementMeta,
  getElementMetaObject,
  storeCompositionTemplateMetadata,
  upsertElementMetaObject,
} from '@src/utility/extraElementData';
import { LogManager } from '@src/utility/Logger';
import {
  buildSpeakersFromMappings,
  extractSpeakerLabelsFromTranscriptElements,
  fetchSpeakerVoiceMappings,
} from '@src/utility/speakerUtils';
import { useFlagsCombination } from '@src/utility/useFlagsCombination';
import { observer } from 'mobx-react-lite';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { useCompositionContext } from '../CompositionContext';
import { useAITextGeneration } from '../CompositionSettings/controls/useAITextGeneration';
import { produceKaraokeTextCore } from '../KaraokeSettings/KaraokeEditorView';
import {
  applyEnabledOnlyChange,
  applyLockPositionChange,
  collectCaptionsPlacement,
  collectHookPlacement,
  collectLogoPlacement,
  collectNamecardPlacement,
  type ConfigAccumulators,
  type EditorElementContext,
  mergeCaptionsReplacement,
  mergeCardReplacement,
  mergeLogoReplacement,
  placementDiffersFromStored,
  type RepoConfigsResult,
  type TemplateConfigContext,
} from './buildTemplateConfigs';
import { useTemplateEditAccess } from './useTemplateEditAccess';
import {
  buildVideoProps,
  cleanupTemplateMetadata,
  createTemplateCallbacks,
  normalizeTemplateId,
  removeElementsAndMetadata,
  removeTemplateElementsWithoutSceneAdjustment,
} from './videoTemplateHelpers';
import {
  collectMissingKaraokeTemplates,
  configureTemplateForAspectRatio,
  createTemplateWithReplacedElement,
  getCurrentOverrideConfigurationKey,
} from './videoTemplateUtils';

import type {
  AITextControlConfiguration,
  CompositionTemplate,
} from '../CompositionSettings/types';
import type {
  VideoTemplate,
  VideoTemplateConfig,
  VideoTemplateConfigured,
} from './types';
import type { UpdateTemplatePlacementResult } from './UpdateTemplatePlacementModal';
import type { Speaker } from '@src/components/content-components/SpeakerTypes';
import type { Template } from '@src/components/textProcessor/TextBrandingService';
import type { Track, VideoEditor } from '@src/editor';
import type { IVideoEditor } from '@src/editor/interface/IVideoEditor';
import type { IVideoCreatorStore } from '@src/stores/IVideoCreatorStore';
import type { EditorStoreLike } from '@src/utility/compositionSettings';
import type { ReactNode } from 'react';

const logger = LogManager.getLogger('videoTemplate:VideoTemplatesContext');

export type VideoTemplateElementType =
  | 'logo'
  | 'introCard'
  | 'endCard'
  | 'captions'
  | 'nameCard'
  /** PLAT-11034 — the template's hook: an overlay on `clip` / `clip-start`. */
  | 'hook';

export type VideoTemplateElementEntry = {
  id: string; //actually always string but we need to support other types defined in Element type
  type: VideoTemplateElementType;
  /** For nameCard elements, tracks which speaker this namecard is for */
  speakerLabel?: string;
  /** For nameCard elements, tracks the time (in seconds) when the card was inserted */
  time?: number;
};

export type VideoTemplateSettings = {
  activeVideoTemplateId: string;
  videoTemplateElements: VideoTemplateElementEntry[];
  replacedElementIds?: {
    [key in VideoTemplateElementType]?: string;
  };
  /** DatoCMS upload ID for replaced logo (needed for persisting logo replacement) */
  replacedLogoUploadId?: string;
};

export const VIDEO_TEMPLATE_SETTINGS_KEY = 'video_template_settings';

/** Placeholder name a newly created template carries until the user renames it. */
export const DRAFT_TEMPLATE_LABEL = 'Draft - Template';

interface VideoTemplatesContextType {
  videoTemplates: VideoTemplateConfigured[];
  isLoading: boolean;
  addVideoTemplate: (
    videoTemplate: VideoTemplateConfigured,
    force?: boolean,
  ) => Promise<void>;
  clearTemplate: () => Promise<void>;
  activeTemplate: VideoTemplateConfigured | undefined;
  activeTemplateSettings: VideoTemplateSettings | undefined;
  setActiveTemplateSettings: (
    settings: VideoTemplateSettings | undefined,
  ) => void;
  currentTab: VideoTemplateTab;
  setCurrentTab: (tab: VideoTemplateTab) => void;
  toggleTemplateElement: (
    elementType: VideoTemplateElementType,
  ) => Promise<void>;
  saveIfCurrentVideoLatest: () => Promise<void>;
  /** Resolves to the ids of the elements it added (empty when none were). */
  replaceTemplateElement: (
    elementType: VideoTemplateElementType,
    newCompositionTemplateId?: string,
    uploadId?: string,
  ) => Promise<string[]>;
  getReplacedElementId: (
    elementType: VideoTemplateElementType,
  ) => string | undefined;
  /**
   * Make the karaoke branding list complete for the current aspect ratio before
   * reading it. Call before opening a surface that lists captions styles.
   */
  syncKaraokeBrandingTemplates: () => void;
  speakers: Speaker[];
  videoCreator: IVideoCreatorStore;
  editor: IVideoEditor;
  /** Check for new speakers and add namecards for them. Debounced - safe to call frequently. */
  checkAndAddNamecardsForNewSpeakers: () => void;
  isClipMode: boolean;
  /** Update element enabled states and/or placement(s) in the Dato CMS template */
  updateTemplateConfig: (
    result: UpdateTemplatePlacementResult,
  ) => Promise<void>;
  /**
   * Whether the live editor placement for an element deviates from what the
   * template stores — used to default the placement toggle in the update modal.
   */
  getPlacementDeviation: (type: VideoTemplateElementType) => boolean;
  hasAccessToEditAll: boolean;
  hasAccessToEditThisTemplate: boolean;
  /**
   * Create a new empty template named {@link DRAFT_TEMPLATE_LABEL}, link it to
   * the showcase's customer type and open its settings — the user renames it
   * from there rather than up front.
   */
  createNewTemplate: () => Promise<void>;
  /** Rename an existing template */
  renameTemplate: (templateId: string, label: string) => Promise<void>;
  /** Save the current template as a new copy with modifications applied */
  saveTemplateAsCopy: (
    result: UpdateTemplatePlacementResult,
    label: string,
  ) => Promise<void>;
}
export const VideoTemplatesContext =
  createContext<VideoTemplatesContextType | null>(null);

export const useVideoTemplatesContext = () => {
  const context = useContext(VideoTemplatesContext);
  if (!context) {
    throw new Error(
      'useVideoTemplatesContext must be used within a VideoTemplatesProvider',
    );
  }
  return context;
};

interface VideoTemplatesProviderProps {
  children: ReactNode;
  videoCreator: IVideoCreatorStore;
  editorStore: EditorStoreLike;
  /** When true, operates in clip mode with limited functionality (no global store access) */
  isClipMode?: boolean;
}

export enum VideoTemplateTab {
  GRID = 'grid',
  SETTINGS = 'settings',
}

export const VideoTemplatesProvider: React.FC<VideoTemplatesProviderProps> =
  observer(({ children, videoCreator, editorStore, isClipMode = false }) => {
    const { datoClientStore } = useStore();
    const userIdentity = useUserIdentity();
    const datoPerson = useDatoPerson();

    const saveIfCurrentVideoLatest = useCallback(async () => {
      // A newly created video (e.g. from an aspect ratio change) may not
      // have its version history loaded yet, so isCurrentVideoTheLatest
      // returns false even though it IS the latest. Treat "no versions
      // loaded" as "latest" so template settings aren't lost.
      const versionsLoaded =
        (videoCreator.currentVideoVersions?.length ?? 0) > 0;
      if (versionsLoaded && !videoCreator.isCurrentVideoTheLatest) {
        return;
      }

      // Call saveStoryAndVideo directly — its internal saveCurrentAndParentVideos
      // queues behind any in-flight save instead of silently dropping the request.
      await videoCreator.saveStoryAndVideo(false, true, false);
    }, [videoCreator]);

    // Use global hook for normal mode, create local function for clip mode
    // Pass videoCreator as override to ensure logo metadata is stored in the correct extraElementData
    const { addMediaToTimeline } = useAddMediaToTimeline({
      editorStore,
      videoCreator,
    });

    // Import composition context to get access to composition templates
    const { getCompositionById } = useCompositionContext();

    // PLAT-11034 — so a template element carrying an `aiText` control (the hook)
    // arrives written rather than showing the template's placeholder. Same hook
    // the overlay-insert surfaces use; the template path is just another
    // insertion path (E12).
    const aiTextGenerationContext = useMemo(
      () => ({
        storyId: videoCreator.storyId ?? undefined,
        showcaseId: videoCreator.organization?.id,
        clipId: videoCreator.currentVideo?.id,
      }),
      [
        videoCreator.storyId,
        videoCreator.organization?.id,
        videoCreator.currentVideo?.id,
      ],
    );
    const getCurrentVideoForAIText = useCallback(
      () => videoCreator.currentVideo,
      [videoCreator],
    );
    const refreshSourceForAIText = useCallback(
      () => videoCreator.videoSourceStore?.refreshSource(),
      [videoCreator],
    );
    const getTranscriptForAIText = useCallback(
      () => editorStore.editor.getFullTranscript(),
      [editorStore],
    );
    const aiTextGeneration = useAITextGeneration({
      surface: 'creator-studio-video-template',
      editorStore,
      getCurrentVideo: getCurrentVideoForAIText,
      refreshSource: refreshSourceForAIText,
      context: aiTextGenerationContext,
      // A GETTER, not a value: `getFullTranscript()` walks every word and is
      // uncached, and this provider re-renders often — resolving it lazily means
      // it runs once per generation instead of once per render, and the
      // generation always reads the current transcript.
      transcriptElements: getTranscriptForAIText,
    });
    const onAITextControlsCreated = useCallback(
      (
        elementId: string,
        controls: ReadonlyArray<AITextControlConfiguration>,
      ) =>
        aiTextGeneration.generateForNewComposition({
          elementId,
          controls,
          // The hook cards read the CLIP, so there is no selection to resolve.
          overlay: null,
        }),
      [aiTextGeneration],
    );
    const [allVideoTemplates, setAllVideoTemplates] = useState<VideoTemplate[]>(
      [],
    );
    const [currentTab, setCurrentTab] = useState<VideoTemplateTab>(() => {
      const settings = getElementMetaObject(
        videoCreator.currentVideo,
        VIDEO_TEMPLATE_SETTINGS_KEY,
      );
      return settings ? VideoTemplateTab.SETTINGS : VideoTemplateTab.GRID;
    });
    const [isLoading, setIsLoading] = useState(false);
    const [isApplyingTemplate, setIsApplyingTemplate] = useState(false);
    const [activeTemplateSettings, setActiveTemplateSettings] = useState<
      VideoTemplateSettings | undefined
    >(undefined);
    const {
      creatorStudioEnableDesignTabs,
      creatorStudioEnableVideoTemplatesTab,
    } = useFlagsCombination(userIdentity.currentRole);

    // Sync namecards when transcript speakers change
    // Access transcriptElements directly to enable MobX reactivity tracking
    const transcriptElements =
      videoCreator.rootStore?.transcriptionStore?.transcriptionElements;
    const currentVideoId = videoCreator.currentVideo?.id ?? null;

    // Compute speaker signature during render (inside observer()) so MobX
    // properly tracks deep changes to transcript elements. Using the raw
    // MobX array reference as a useEffect dependency doesn't reliably
    // trigger because the proxy reference may stay the same even when
    // contents change. A derived string value solves this.
    const currentSpeakerSignature = getSpeakerSignature(transcriptElements);

    const prevSpeakerSignatureRef = useRef<string | null>(null);
    const hasCapturedInitialSignatureRef = useRef(false);
    const lastSignatureVideoRef = useRef<string | null>(null);

    const {
      setDefaultKaraokeConfigId,
      updateKaraokeConfigAndProduce,
      commandProduceKaraoke,
      commandDeleteKaraoke,
      getKaraokeConfigForKaraokeClipId,
    } = useKaraoke(editorStore.editor);

    const karaokeStore = useKaraokeStore();

    const { zoomToFit } = useTimeline({
      duration: videoCreator.duration,
      frameRate: videoCreator.timelineStore.framerate,
      enableKeyboardShortcuts: true,
      autoScroll: true,
    });

    const currentAspectRatio = videoCreator.getCurrentAspectRatio();

    // ============================================================================
    // SPEAKERS
    // ============================================================================

    const [speakers, setSpeakers] = useState<Speaker[]>([]);

    useEffect(() => {
      const storyId = videoCreator.story?.id;
      if (!storyId) return;
      const loadSpeakers = async () => {
        try {
          const transcriptElementsVisible = transcriptElements?.filter(
            el => el.state !== 'removed' && el.state !== 'cut',
          );
          const labels = extractSpeakerLabelsFromTranscriptElements(
            transcriptElementsVisible,
          );

          if (labels.length === 0) {
            setSpeakers([]);
            return;
          }
          const [speakerMappings, datoSpeakers] = await Promise.all([
            fetchSpeakerVoiceMappings(storyId),
            videoCreator.datoClientStore?.speakerRepository?.getStorySpeakers(
              storyId,
            ),
          ]);

          const built = buildSpeakersFromMappings(
            labels,
            speakerMappings,
            datoSpeakers.storySpeakers,
          );
          setSpeakers(built);
        } catch (error) {
          LogManager.getLogger('component:VideoTemplateSettings').warn(
            'Failed to load speakers',
            { error },
          );
        }
      };
      loadSpeakers();
    }, [
      videoCreator.story?.id,
      videoCreator.currentVideo?.id,
      videoCreator.datoClientStore,
      videoCreator.rootStore,
      transcriptElements,
    ]);

    // ============================================================================
    // COMPUTED VALUES & MEMOIZED DATA
    // ============================================================================

    const videoTemplatesConfigured = useMemo(
      () =>
        allVideoTemplates.map(t =>
          configureTemplateForAspectRatio(
            t,
            currentAspectRatio,
            getCompositionById,
          ),
        ),
      // getCompositionById re-identifies on every composition write, which is what
      // pulls the freshly saved composition into each template's card configs.
      [allVideoTemplates, currentAspectRatio, getCompositionById],
    );

    const activeTemplate = useMemo(() => {
      return videoTemplatesConfigured.find(
        template =>
          template.id === activeTemplateSettings?.activeVideoTemplateId,
      );
    }, [
      videoTemplatesConfigured,
      activeTemplateSettings?.activeVideoTemplateId,
    ]);

    const { hasAccessToEditAll, hasAccessToEditThisTemplate } =
      useTemplateEditAccess({
        identity: userIdentity,
        albumRepository: datoClientStore.albumRepository,
        storyId: videoCreator.story?.id,
        activeTemplate,
      });

    /**
     * Brings `brandingTemplates` up to date for the current aspect ratio: the
     * showcase's own karaoke brandings and the global presets, plus the records
     * only video templates reference.
     *
     * Both halves are needed on every call because the two writers clobber each
     * other — `loadAllTemplates` replaces the array wholesale, dropping anything
     * unshifted here, and it only ever runs from the karaoke sidebar. A surface
     * that lists captions styles (the replace modal) must call this first, or it
     * shows whichever half happens to be there.
     */
    const syncKaraokeBrandingTemplates = useCallback(() => {
      const textBrandingService = videoCreator.textBrandingService;
      textBrandingService.ensureTemplatesLoaded(
        'karaoke',
        videoCreator.getCurrentAspectRatio(),
      );

      const newTemplates = collectMissingKaraokeTemplates(
        videoTemplatesConfigured,
        textBrandingService.brandingTemplates.map(t => t.id as string),
      );
      if (newTemplates.length > 0) {
        textBrandingService.brandingTemplates.unshift(
          ...newTemplates.map(t => t as Template),
        );
      }
    }, [videoCreator, videoTemplatesConfigured]);

    // Re-runs once the global presets land: they are fetched during startup, so
    // the first pass usually happens before they exist and builds nothing. Not
    // the mapped list's length — an environment with no global presets answers
    // with an empty one, a count that never changes. `null` until the request
    // comes back covers both that and the ordinary case.
    const loadedKaraokePresetCount =
      videoCreator.textBrandingService.globalKaraokePresetRecords?.length ??
      null;

    useEffect(() => {
      syncKaraokeBrandingTemplates();
    }, [syncKaraokeBrandingTemplates, loadedKaraokePresetCount]);

    // ============================================================================
    // HELPER FUNCTIONS - Simple Utilities
    // ============================================================================

    const getReplacedElementId = useCallback(
      (elementType: VideoTemplateElementType): string | undefined => {
        return activeTemplateSettings?.replacedElementIds?.[elementType];
      },
      [activeTemplateSettings],
    );

    const resetTimelineToFit = useCallback(() => {
      const allElements = [...editorStore.editor.timeline.allElements];

      if (allElements.length === 0) {
        videoCreator.setDuration(0);
        return;
      }

      const earliestElement = allElements.reduce((acc, curr) =>
        acc.time < curr.time ? acc : curr,
      );

      const latestElement = allElements.reduce((acc, curr) =>
        acc.end_time > curr.end_time ? acc : curr,
      );

      editorStore.editor.commandShiftMultipleElements(
        allElements.map(e => e.id),
        earliestElement.time * -1,
        false,
        false,
      );

      videoCreator.setDuration(latestElement.end_time - earliestElement.time);
    }, [editorStore, videoCreator]);

    const updateTemplateSettingsWithFilter = useCallback(
      (
        filterFn: (element: {
          id: string;
          type: VideoTemplateElementType;
        }) => boolean,
      ) => {
        let nextValue: VideoTemplateSettings | undefined;
        setActiveTemplateSettings(prev => {
          if (!prev) {
            nextValue = undefined;
            return prev;
          }

          const newVideoTemplateElements =
            prev.videoTemplateElements.filter(filterFn);

          nextValue = {
            ...prev,
            videoTemplateElements: newVideoTemplateElements,
          };
          return nextValue;
        });

        if (nextValue) {
          upsertElementMetaObject(
            videoCreator.currentVideo,
            VIDEO_TEMPLATE_SETTINGS_KEY,
            nextValue,
          );
        }
      },
      [videoCreator],
    );

    const updateSettingsWithReplacedElement = useCallback(
      (
        videoTemplateElements: VideoTemplateElementEntry[],
        elementType: VideoTemplateElementType,
        newElementData: string | undefined,
        logoUploadId?: string,
      ) => {
        const updatedSettings: VideoTemplateSettings = {
          ...activeTemplateSettings!,
          videoTemplateElements,
          replacedElementIds: {
            ...(activeTemplateSettings!.replacedElementIds || {}),
            [elementType]: newElementData,
          },
          ...(logoUploadId ? { replacedLogoUploadId: logoUploadId } : {}),
        };

        upsertElementMetaObject(
          videoCreator.currentVideo,
          VIDEO_TEMPLATE_SETTINGS_KEY,
          updatedSettings,
        );
        setActiveTemplateSettings(updatedSettings);
      },
      [activeTemplateSettings, videoCreator],
    );

    // ============================================================================
    // KARAOKE-SPECIFIC FUNCTIONS
    // ============================================================================

    const applyKaraokeTemplate = async (
      template: Template,
      createUndo: boolean,
    ) => {
      const config = editorStore.editor.getDefaultKaraokeConfig();
      const karaokeGenerated = editorStore.editor.getKaraokeLines().length > 0;

      // Disable auto-switch to karaoke sidebar when applying from video template
      const previousAutoSwitch = videoCreator.autoSwitchToKaraokeSidebar;
      const previousShowSubtitles = videoCreator.getShowSubtitles();
      videoCreator.autoSwitchToKaraokeSidebar = false;

      try {
        await produceKaraokeTextCore(undefined, {
          analytics,
          videoCreator,
          config: config!,
          isKaraokeGenerated: karaokeGenerated,
          karaokeProducer: videoCreator.karaokeProducer,
          textBrandingService: videoCreator.textBrandingService,
          setDefaultKaraokeConfigId,
          updateKaraokeConfigAndProduce,
          commandProduceKaraoke: () =>
            commandProduceKaraoke({
              replaceTrack: true,
              createUndoRedoEntry: createUndo,
            }),
        });
        // TODO: textBrandingService modifies template in global EditorStore
        // not working in Clip Preview
        videoCreator.textBrandingService.applyTemplate(
          template,
          'karaoke',
          null,
          undefined,
          false,
          false,
        );
      } finally {
        // Restore the previous auto-switch and showSubtitles settings
        // (produceKaraokeTextCore forces showSubtitles=true, which is
        // unwanted when auto-applying a template on clip preview open)
        videoCreator.autoSwitchToKaraokeSidebar = previousAutoSwitch;
        videoCreator.setShowSubtitles(previousShowSubtitles);
      }
    };

    const applyReplacedKaraokeTemplate = useCallback(
      async (templateId: string): Promise<boolean> => {
        try {
          const karaokeTemplate =
            videoCreator.textBrandingService.brandingTemplates.find(
              t => t.id === templateId && t.type === 'karaoke',
            ) ||
            videoCreator.textBrandingService.globalKaraokePresets.find(
              t => t.id === templateId,
            );

          if (!karaokeTemplate || !activeTemplateSettings) return false;

          await applyKaraokeTemplate(karaokeTemplate, true);

          const elementId = normalizeTemplateId(karaokeTemplate.id);

          const updatedSettings: VideoTemplateSettings = {
            ...activeTemplateSettings,
            videoTemplateElements: [
              ...activeTemplateSettings.videoTemplateElements,
              { id: elementId, type: 'captions' as const },
            ],
          };

          upsertElementMetaObject(
            videoCreator.currentVideo,
            VIDEO_TEMPLATE_SETTINGS_KEY,
            updatedSettings,
          );
          setActiveTemplateSettings(updatedSettings);
          return true;
        } catch (error) {
          logger.error('Error applying karaoke template', { error });
          return false;
        }
      },
      [activeTemplateSettings, applyKaraokeTemplate, videoCreator],
    );

    // ============================================================================
    // TEMPLATE MODIFICATION FUNCTIONS
    // ============================================================================

    const getTemplateWithReplacement = useCallback(
      async (
        elementType: VideoTemplateElementType,
        replacedElementId: string | undefined,
      ): Promise<VideoTemplateConfigured> => {
        if (!replacedElementId || !activeTemplate) return activeTemplate!;

        if (elementType === 'logo') {
          const template = await createTemplateWithReplacedElement(
            activeTemplate,
            elementType,
            replacedElementId,
            getCompositionById,
          );
          logger.info('Logo template modified with replaced URL', {
            originalUrl:
              activeTemplate.configuration?.logoConfiguration?.logo?.url,
            replacedUrl: template.configuration?.logoConfiguration?.logo?.url,
            replacedElementId,
          });
          return template;
        }

        if (getCompositionById) {
          return await createTemplateWithReplacedElement(
            activeTemplate,
            elementType,
            replacedElementId,
            getCompositionById,
          );
        }

        return activeTemplate;
      },
      [activeTemplate, getCompositionById],
    );

    // ============================================================================
    // ELEMENT REMOVAL FUNCTIONS
    // ============================================================================

    const removeCurrentElements = useCallback(
      (
        currentlyEnabled: { id: string; type: VideoTemplateElementType }[],
        elementType: VideoTemplateElementType,
        createUndo: boolean,
      ): { id: string; type: VideoTemplateElementType }[] => {
        const videoTemplateElements = [
          ...activeTemplateSettings!.videoTemplateElements,
        ];

        if (elementType === 'captions') {
          commandDeleteKaraoke(false);
        } else {
          removeElementsAndMetadata(
            currentlyEnabled,
            editorStore,
            videoCreator,
            () => {}, // No karaoke deletion for non-caption elements
            createUndo,
          );
        }

        return videoTemplateElements.filter(
          e => !currentlyEnabled.some(ce => ce.id === e.id),
        );
      },
      [activeTemplateSettings, editorStore, videoCreator, commandDeleteKaraoke],
    );

    const removeActiveTemplateElements = useCallback(async () => {
      if (
        activeTemplateSettings?.activeVideoTemplateId &&
        activeTemplateSettings.videoTemplateElements.length > 0
      ) {
        // Remove template elements WITHOUT triggering scene adjustment
        // This preserves user-created scenes when swapping templates
        removeTemplateElementsWithoutSceneAdjustment(
          activeTemplateSettings.videoTemplateElements,
          editorStore,
          videoCreator,
          commandDeleteKaraoke,
        );
      }

      resetTimelineToFit();
    }, [
      activeTemplateSettings,
      editorStore,
      videoCreator,
      commandDeleteKaraoke,
      resetTimelineToFit,
    ]);

    // ============================================================================
    // ELEMENT ENABLE/DISABLE FUNCTIONS
    // ============================================================================

    const disableTemplateElements = useCallback(
      (currentlyEnabled: { id: string; type: VideoTemplateElementType }[]) => {
        removeElementsAndMetadata(
          currentlyEnabled,
          editorStore,
          videoCreator,
          commandDeleteKaraoke,
          true,
        );

        const videoTemplateElements =
          activeTemplateSettings!.videoTemplateElements.filter(
            e => !currentlyEnabled.some(ce => ce.id === e.id),
          );

        const updatedSettings: VideoTemplateSettings = {
          ...activeTemplateSettings!,
          videoTemplateElements,
        };

        upsertElementMetaObject(
          videoCreator.currentVideo,
          VIDEO_TEMPLATE_SETTINGS_KEY,
          updatedSettings,
        );
        setActiveTemplateSettings(updatedSettings);
      },
      [activeTemplateSettings, editorStore, videoCreator, commandDeleteKaraoke],
    );

    const enableTemplateElements = useCallback(
      async (elementType: VideoTemplateElementType) => {
        const replacedElementId = getReplacedElementId(elementType);

        // Special handling for captions with replaced template
        if (elementType === 'captions' && replacedElementId) {
          const success = await applyReplacedKaraokeTemplate(replacedElementId);
          if (success) return;
        }

        const templateToUse = await getTemplateWithReplacement(
          elementType,
          replacedElementId,
        );

        const videoProps = buildVideoProps(videoCreator, editorStore);

        const result = await addSingleElementType({
          videoTemplate: templateToUse,
          elementType,
          editorStore,
          videoProps,
          upsertExtraElementDataCb: (
            elementId: string,
            template: CompositionTemplate,
          ) => {
            storeCompositionTemplateMetadata(
              videoCreator.currentVideo,
              elementId,
              template,
            );
          },
          addMediaToTimelineCb: (item, mediaType, options, createUndo) =>
            addMediaToTimeline(item, mediaType, options, false, createUndo),
          applyKaraokeTemplate,
          rootStore: videoCreator.rootStore,
          onAITextControlsCreated,
        });

        if (result.ok) {
          const updatedSettings: VideoTemplateSettings = {
            ...activeTemplateSettings!,
            videoTemplateElements: [
              ...activeTemplateSettings!.videoTemplateElements,
              ...result.value,
            ],
          };

          upsertElementMetaObject(
            videoCreator.currentVideo,
            VIDEO_TEMPLATE_SETTINGS_KEY,
            updatedSettings,
          );
          setActiveTemplateSettings(updatedSettings);
        }
      },
      [
        activeTemplateSettings,
        editorStore,
        videoCreator,
        addMediaToTimeline,
        applyKaraokeTemplate,
        getReplacedElementId,
        applyReplacedKaraokeTemplate,
        getTemplateWithReplacement,
        onAITextControlsCreated,
      ],
    );

    // ============================================================================
    // PUBLIC API FUNCTIONS - Main Operations
    // ============================================================================

    const handleAddVideoTemplate = useCallback(
      async (
        videoTemplate: VideoTemplateConfigured,
        force = false,
        skipSave = false,
        skipUndo = false,
      ) => {
        // Prevent double-clicks from applying multiple templates
        if (
          !force &&
          videoTemplate.id === activeTemplateSettings?.activeVideoTemplateId
        ) {
          logger.warn('Template already applied');
          setCurrentTab(VideoTemplateTab.SETTINGS);
          return;
        }
        if (isApplyingTemplate) {
          logger.warn('Template application already in progress');
          return;
        }

        setIsApplyingTemplate(true);
        try {
          videoCreator.isLoading = true;

          // Store previous template settings before removal for undo/redo tracking
          await removeActiveTemplateElements();

          const videoProps = buildVideoProps(videoCreator, editorStore);
          const callbacks = createTemplateCallbacks(
            videoCreator,
            addMediaToTimeline,
          );

          const result = await addVideoTemplate({
            videoTemplate,
            editorStore,
            videoProps,
            ...callbacks,
            applyKaraokeTemplate,
            previousVideoTemplate: activeTemplateSettings,
            rootStore: videoCreator.rootStore,
            skipUndo,
            onAITextControlsCreated,
          });

          if (result.ok) {
            const updatedSettings: VideoTemplateSettings = {
              activeVideoTemplateId: videoTemplate.id,
              videoTemplateElements: result.value.videoTemplateElements,
              replacedElementIds: {},
            };

            logger.info('Template applied, saving settings', {
              updatedSettings,
              videoId: videoCreator.currentVideo?.id,
            });

            // Update duration to encompass all elements before zooming to fit
            const allElements = [...editorStore.editor.timeline.allElements];
            if (allElements.length > 0) {
              const maxEndTime = allElements.reduce(
                (max, el) => Math.max(max, el.end_time),
                0,
              );
              await videoCreator.setDuration(maxEndTime);
            }

            zoomToFit();

            // Clean up previous template metadata
            const previousElements =
              activeTemplateSettings?.videoTemplateElements || [];
            if (previousElements.length > 0) {
              cleanupTemplateMetadata(videoCreator, previousElements);
            }

            setActiveTemplateSettings(updatedSettings);
            if (!skipSave) {
              await saveIfCurrentVideoLatest();
            }
          }
        } finally {
          setIsApplyingTemplate(false);
          videoCreator.isLoading = false;
        }
      },
      [
        activeTemplateSettings,
        videoCreator,
        removeActiveTemplateElements,
        editorStore,
        addMediaToTimeline,
        applyKaraokeTemplate,
        zoomToFit,
        isApplyingTemplate,
        saveIfCurrentVideoLatest,
      ],
    );

    // ============================================================================
    // AUTO-APPLY TEMPLATE FROM CLIP METADATA
    // ============================================================================

    // Track if we've already auto-applied in this session to prevent loops
    const hasAutoAppliedRef = useRef(false);
    // Track which video the auto-apply was performed for so we reset on video switch
    const autoAppliedForVideoRef = useRef<string | null>(null);

    // Helper to check if extraElementData is empty (no modifications)
    const isExtraElementDataEmpty = useCallback(
      (data: Record<string, unknown> | undefined): boolean => {
        return !data || !Object.keys(data).length;
      },
      [],
    );

    // Auto-apply template from clip metadata if no modifications exist
    useEffect(() => {
      const autoApplyVideoTemplateId =
        videoCreator.currentVideo?.clipJson?.videoTemplateId;
      const clipExtraElementData = videoCreator.currentVideo?.extraElementData;

      // Reset per-video: allow auto-apply again when switching to a different video.
      // Note: we don't update autoAppliedForVideoRef here — it's updated only on
      // successful auto-apply so that videoChanged stays true until the first
      // *eligible* run (avoids stale activeTemplateSettings blocking the apply).
      const videoChanged = autoAppliedForVideoRef.current !== currentVideoId;
      if (videoChanged) {
        hasAutoAppliedRef.current = false;
      }

      // Only auto-apply if:
      // 1. Has autoApplyVideoTemplateId
      // 2. Templates are loaded
      // 3. No modifications exist (extraElementData is empty {})
      // 4. Haven't already auto-applied in this session
      // 5. Not currently applying a template
      // 6. No pending template re-apply
      // 7. Video creator is initialized
      if (
        autoApplyVideoTemplateId &&
        !isLoading &&
        videoTemplatesConfigured.length > 0 &&
        isExtraElementDataEmpty(clipExtraElementData) &&
        !hasAutoAppliedRef.current &&
        !isApplyingTemplate &&
        !videoCreator.pendingTemplateReapplyId &&
        videoCreator.isInitialized
      ) {
        const template = videoTemplatesConfigured.find(
          t => t.id === autoApplyVideoTemplateId,
        );
        if (template) {
          logger.info('Auto-applying template from clip metadata', {
            templateId: autoApplyVideoTemplateId,
          });
          autoAppliedForVideoRef.current = currentVideoId;
          hasAutoAppliedRef.current = true;
          // When the video just changed, activeTemplateSettings may still hold
          // stale data from the previous video (React setState is async), so
          // force=true bypasses the "already applied" guard. On the same video
          // we keep force=false to preserve double-apply protection.
          handleAddVideoTemplate(template, videoChanged, false, true);
        }
      }
    }, [
      isLoading,
      videoTemplatesConfigured,
      isExtraElementDataEmpty,
      isApplyingTemplate,
      handleAddVideoTemplate,
      videoCreator.isInitialized,
      videoCreator.currentVideo,
    ]);

    // Re-apply template fresh after aspect ratio change.
    // The store strips template elements and sets pendingTemplateReapplyId
    // so they can be re-applied with the correct aspect-specific config.
    useEffect(() => {
      const pendingId = videoCreator.pendingTemplateReapplyId;

      if (!pendingId || !videoCreator.isInitialized) return;
      if (isLoading || videoTemplatesConfigured.length === 0) return;
      if (isApplyingTemplate) return;

      const template = videoTemplatesConfigured.find(t => t.id === pendingId);
      if (!template) {
        logger.warn('Pending template not found for re-apply', {
          pendingId,
        });
        videoCreator.pendingTemplateReapplyId = null;
        return;
      }

      logger.info('Re-applying template fresh after aspect ratio change', {
        templateId: pendingId,
      });
      videoCreator.pendingTemplateReapplyId = null;
      handleAddVideoTemplate(template, true);
    }, [
      videoCreator.pendingTemplateReapplyId,
      videoCreator.isInitialized,
      isLoading,
      videoTemplatesConfigured,
      isApplyingTemplate,
      handleAddVideoTemplate,
      videoCreator.currentVideo,
    ]);

    // Wire the reapplyTemplate callback slot for direct (non-deferred) re-apply.
    // Used by ClipVideoCreatorAdapter.loadVideoWithAspectRatio to re-apply
    // the template synchronously within the AR creation flow, before the save.
    //
    // We must NOT close over `videoTemplatesConfigured` here because
    // selectDimension() updates the MobX observable before this callback
    // runs, but React hasn't re-rendered yet — so the useMemo'd
    // videoTemplatesConfigured still has the PREVIOUS AR's overrides.
    // Instead, compute the configured template on the fly using the
    // current AR from the store.
    useEffect(() => {
      videoCreator.reapplyTemplate = async (templateId: string) => {
        const rawTemplate = allVideoTemplates.find(t => t.id === templateId);
        if (!rawTemplate) {
          logger.warn('reapplyTemplate: template not found', { templateId });
          return;
        }

        // Compute AR-specific configuration at call time so we always
        // use the correct overrides for the newly selected aspect ratio
        // (the useMemo'd videoTemplatesConfigured may still reflect the
        // previous AR because React hasn't re-rendered yet).
        const ar = videoCreator.getCurrentAspectRatio();
        const template = configureTemplateForAspectRatio(
          rawTemplate,
          ar,
          getCompositionById,
        );

        logger.info('reapplyTemplate: direct re-apply for AR change', {
          templateId,
          aspectRatio: ar,
        });
        await handleAddVideoTemplate(template, true, true, true);
      };

      return () => {
        videoCreator.reapplyTemplate = undefined;
      };
    }, [
      videoCreator,
      allVideoTemplates,
      handleAddVideoTemplate,
      getCompositionById,
    ]);

    const clearTemplate = useCallback(async () => {
      if (!activeTemplateSettings) {
        logger.warn('Cannot clear template: no active template');
        return;
      }

      // Prevent action while template operation is in progress
      if (isApplyingTemplate) {
        logger.warn('Template operation already in progress');
        return;
      }

      setIsApplyingTemplate(true);
      try {
        videoCreator.isLoading = true;
        // Remove template elements WITHOUT triggering scene adjustment
        // This preserves user-created scenes that may be anchored to template elements
        removeTemplateElementsWithoutSceneAdjustment(
          activeTemplateSettings.videoTemplateElements,
          editorStore,
          videoCreator,
          commandDeleteKaraoke,
        );

        // Shift remaining content to start of timeline
        resetTimelineToFit();

        // Clean up template metadata
        const previousElements =
          activeTemplateSettings.videoTemplateElements || [];
        if (previousElements.length > 0) {
          cleanupTemplateMetadata(videoCreator, previousElements);
        }

        // Delete the template settings from element meta
        deleteElementMeta(
          videoCreator.currentVideo,
          VIDEO_TEMPLATE_SETTINGS_KEY,
        );
        // Reset state
        setActiveTemplateSettings(undefined);
        setCurrentTab(VideoTemplateTab.GRID);
        await saveIfCurrentVideoLatest();
      } finally {
        setIsApplyingTemplate(false);
        videoCreator.isLoading = false;
      }
    }, [
      activeTemplateSettings,
      videoCreator,
      editorStore,
      commandDeleteKaraoke,
      setCurrentTab,
      isApplyingTemplate,
      resetTimelineToFit,
      saveIfCurrentVideoLatest,
    ]);

    const toggleTemplateElement = useCallback(
      async (elementType: VideoTemplateElementType) => {
        if (!activeTemplate || !activeTemplateSettings) {
          logger.warn('Cannot toggle element: no active template');
          return;
        }

        const currentlyEnabled =
          activeTemplateSettings.videoTemplateElements.filter(
            e => e.type === elementType,
          );

        videoCreator.isLoading = true;
        try {
          if (currentlyEnabled.length > 0) {
            disableTemplateElements(currentlyEnabled);
          } else {
            await enableTemplateElements(elementType);
          }
        } finally {
          videoCreator.isLoading = false;
        }
      },
      [
        activeTemplate,
        activeTemplateSettings,
        disableTemplateElements,
        enableTemplateElements,
      ],
    );

    const replaceTemplateElement = useCallback(
      async (
        elementType: VideoTemplateElementType,
        newElementData?: string,
        uploadId?: string,
      ): Promise<string[]> => {
        if (!activeTemplate || !activeTemplateSettings) {
          logger.warn('Cannot replace element: no active template');
          return [];
        }

        videoCreator.isLoading = true;
        const currentlyEnabled =
          activeTemplateSettings.videoTemplateElements.filter(
            e => e.type === elementType,
          );

        // Handle both "add" (no existing elements) and "replace" (existing elements) cases
        const isAdding = currentlyEnabled.length === 0;

        let videoTemplateElements = [
          ...activeTemplateSettings.videoTemplateElements,
        ];

        // Only remove if there are elements to remove (replace case)
        if (!isAdding) {
          videoTemplateElements = removeCurrentElements(
            currentlyEnabled,
            elementType,
            false,
          );
        }

        // Handle captions (both add and replace)
        if (elementType === 'captions' && newElementData) {
          const success = await applyReplacedKaraokeTemplate(newElementData);
          if (success) {
            updateSettingsWithReplacedElement(
              [
                ...videoTemplateElements,
                { id: newElementData, type: 'captions' },
              ],
              elementType,
              newElementData,
            );
          }
          videoCreator.isLoading = false;
          return [];
        }

        // Create template with the new element for non-caption types
        const templateToUse = await getTemplateWithReplacement(
          elementType,
          newElementData,
        );

        const videoProps = buildVideoProps(videoCreator, editorStore);

        const result = await addSingleElementType({
          videoTemplate: templateToUse,
          elementType,
          editorStore,
          videoProps,
          upsertExtraElementDataCb: (
            elementId: string,
            template: CompositionTemplate,
          ) => {
            storeCompositionTemplateMetadata(
              videoCreator.currentVideo,
              elementId,
              template,
            );
          },
          addMediaToTimelineCb: (item, mediaType, options) =>
            addMediaToTimeline(item, mediaType, options, false, false),
          applyKaraokeTemplate,
          rootStore: videoCreator.rootStore,
          onAITextControlsCreated,
        });

        if (result.ok) {
          const newElements = [...videoTemplateElements, ...result.value];
          updateSettingsWithReplacedElement(
            newElements,
            elementType,
            newElementData,
            elementType === 'logo' ? uploadId : undefined,
          );

          // Only cleanup old element metadata if replacing
          if (!isAdding) {
            for (const element of currentlyEnabled) {
              deleteElementMeta(
                videoCreator.currentVideo,
                element.id.toString(),
              );
            }
          }

          await saveIfCurrentVideoLatest();
        }
        videoCreator.isLoading = false;
        return result.ok ? result.value.map(e => e.id) : [];
      },
      [
        activeTemplate,
        activeTemplateSettings,
        editorStore,
        videoCreator,
        addMediaToTimeline,
        applyKaraokeTemplate,
        removeCurrentElements,
        applyReplacedKaraokeTemplate,
        getTemplateWithReplacement,
        updateSettingsWithReplacedElement,
        saveIfCurrentVideoLatest,
        onAITextControlsCreated,
      ],
    );

    // ============================================================================
    // SYNC NAMECARDS WITH SPEAKERS (ADD NEW, REMOVE ORPHANED)
    // ============================================================================

    const checkAndAddNamecardsForNewSpeakers = useCallback(async () => {
      videoCreator.isLoading = true;
      // Never modify historical versions
      if (!videoCreator.isCurrentVideoTheLatest) {
        logger.debug('Viewing historical version, skipping namecard sync');
        videoCreator.isLoading = false;
        return;
      }

      // Skip if no active template or namecards not enabled
      if (!activeTemplate || !activeTemplateSettings) {
        logger.debug('No active template, skipping namecard sync');
        videoCreator.isLoading = false;
        return;
      }

      const namecardConfig =
        activeTemplate.configuration?.namecardConfiguration;
      if (!namecardConfig?.enabled || !namecardConfig?.compositionTemplate) {
        logger.debug('Namecards not enabled in template, skipping sync');
        videoCreator.isLoading = false;
        return;
      }

      // If the user has no namecards on canvas, they've toggled them off —
      // don't auto-add namecards just because timestamps shifted (e.g. introCard toggle).
      const hasNamecardsOnCanvas =
        activeTemplateSettings.videoTemplateElements.some(
          e => e.type === 'nameCard',
        );
      if (!hasNamecardsOnCanvas) {
        logger.debug(
          'No namecards on canvas (user has them off), skipping sync',
        );
        videoCreator.isLoading = false;
        return;
      }

      const videoProps = buildVideoProps(videoCreator, editorStore);
      let currentElements = [...activeTemplateSettings.videoTemplateElements];
      // Get existing namecards with their metadata
      const allNamecards = currentElements.filter(e => e.type === 'nameCard');
      const namecardsWithLabels = allNamecards.filter(e => e.speakerLabel);

      // Compute what changes are needed
      const existingNamecards = namecardsWithLabels.map(e => ({
        elementId: e.id,
        speakerLabel: e.speakerLabel!,
        time: e.time,
      }));

      // Use transcription store elements directly (same source that drives
      // the speaker signature effect trigger) to guarantee the sync sees
      // the same data that detected the speaker change.

      const syncResult = computeNamecardSync({
        transcriptElements: transcriptElements,
        existingNamecards,
        minGapSeconds: parseFloat(
          namecardConfig.compositionTemplate?.source?.duration?.toString() ||
            '5',
        ),
      });

      // Nothing to do
      if (
        syncResult.toAdd.length === 0 &&
        syncResult.toRemove.length === 0 &&
        syncResult.toUpdate.length === 0
      ) {
        videoCreator.isLoading = false;
        return;
      }

      let hasChanges = false;

      // --- STEP 1: Remove namecards that are no longer needed ---
      if (syncResult.toRemove.length > 0) {
        logger.info(
          `Removing ${syncResult.toRemove.length} namecards no longer needed`,
          { elementIds: syncResult.toRemove },
        );

        const elementsToRemove = currentElements.filter(
          e => e.type === 'nameCard' && syncResult.toRemove.includes(e.id),
        );
        removeElementsAndMetadata(
          elementsToRemove,
          editorStore,
          videoCreator,
          () => {}, // No karaoke deletion for namecards
          true,
        );

        currentElements = currentElements.filter(
          e => !syncResult.toRemove.includes(e.id),
        );
        hasChanges = true;
      }

      // --- STEP 2: Update namecards that moved in time (preserves user edits) ---
      if (syncResult.toUpdate.length > 0) {
        const NAMECARD_TIME_EPSILON = 0.2;
        for (const update of syncResult.toUpdate) {
          // Read element's actual current time from the timeline
          const element = editorStore.editor.timeline.getElement(
            update.elementId,
          );
          if (!element) {
            logger.warn(
              'Namecard element not found on timeline during update',
              { elementId: update.elementId },
            );
            continue;
          }

          const actualTime = element.time;
          const desiredTime = update.newTime;
          const delta = Math.abs(desiredTime - actualTime);

          // If ripple already moved the element to the correct time, skip shift
          if (delta > NAMECARD_TIME_EPSILON) {
            editorStore.editor.commandShiftElement(
              update.elementId,
              desiredTime,
              undefined,
              false,
            );
          }

          // Update stored metadata time
          const entry = currentElements.find(e => e.id === update.elementId);
          if (entry) {
            entry.time = desiredTime;
          }
          hasChanges = true;
        }

        logger.info(
          `Updated ${syncResult.toUpdate.length} namecard time metadata`,
          {
            updates: syncResult.toUpdate.map(u => ({
              elementId: u.elementId,
              speaker: u.speakerLabel,
              newTime: u.newTime,
            })),
          },
        );
      }

      // --- STEP 3: Add new namecards ---
      if (syncResult.toAdd.length > 0) {
        const result = await createNamecardsFromSyncResult({
          videoTemplate: activeTemplate,
          editorStore,
          videoProps,
          upsertExtraElementDataCb: (
            elementId: string,
            template: CompositionTemplate,
          ) => {
            storeCompositionTemplateMetadata(
              videoCreator.currentVideo,
              elementId,
              template,
            );
          },
          rootStore: videoCreator.rootStore,
          appearances: syncResult.toAdd,
        });

        if (result.ok && result.value.length > 0) {
          logger.info(
            `Auto-added ${result.value.length} namecards for new speakers`,
            { newSpeakers: result.value.map(e => e.speakerLabel) },
          );
          currentElements = [...currentElements, ...result.value];
          hasChanges = true;
        }
      }

      // --- STEP 4: Save if any changes were made ---
      if (hasChanges) {
        const updatedSettings: VideoTemplateSettings = {
          ...activeTemplateSettings,
          videoTemplateElements: currentElements,
        };

        upsertElementMetaObject(
          videoCreator.currentVideo,
          'video_template_settings',
          updatedSettings,
        );
        setActiveTemplateSettings(updatedSettings);
        await saveIfCurrentVideoLatest();
      }
      videoCreator.isLoading = false;
    }, [
      activeTemplate,
      activeTemplateSettings,
      transcriptElements,
      videoCreator,
      editorStore,
      saveIfCurrentVideoLatest,
    ]);

    // ============================================================================
    // EFFECTS - Side Effects & Lifecycle
    // ============================================================================

    useEffect(() => {
      // Skip if not ready
      if (!videoCreator.isInitialized || videoCreator.isLoading) return;
      if (!activeTemplateSettings?.activeVideoTemplateId) return;
      if (!activeTemplate?.configuration?.namecardConfiguration?.enabled)
        return;
      // Skip during template application - the template itself handles initial namecard creation
      if (!videoCreator.rootStore?.transcriptionStore.isTranscriptionReady)
        return;
      // Never auto-sync namecards when viewing a historical version
      if (!videoCreator.isCurrentVideoTheLatest) return;
      if (isApplyingTemplate) return;

      const currentSignatureVideoId = videoCreator.currentVideo?.id ?? null;

      // Reset per-video so the first transcript hydrate does not trigger a save
      if (lastSignatureVideoRef.current !== currentSignatureVideoId) {
        lastSignatureVideoRef.current = currentSignatureVideoId;
        hasCapturedInitialSignatureRef.current = false;
        prevSpeakerSignatureRef.current = null;
      }

      // Skip the first signature we see for a video (initial load)
      if (!hasCapturedInitialSignatureRef.current) {
        hasCapturedInitialSignatureRef.current = true;
        prevSpeakerSignatureRef.current = currentSpeakerSignature;
        return;
      }

      // Skip if signature hasn't changed (guards against extra runs from
      // other dependency changes like checkAndAddNamecardsForNewSpeakers)
      if (prevSpeakerSignatureRef.current === currentSpeakerSignature) {
        return;
      }

      logger.debug('Active speakers changed, triggering namecard sync', {
        previous: prevSpeakerSignatureRef.current,
        current: currentSpeakerSignature,
      });
      prevSpeakerSignatureRef.current = currentSpeakerSignature;

      checkAndAddNamecardsForNewSpeakers();
      // currentSpeakerSignature is a derived string computed during render
      // inside observer(), so MobX deep-change tracking reliably updates it.
      // Using it instead of the raw MobX array reference fixes the issue
      // where useEffect's Object.is() comparison missed content changes
      // because the observable array proxy reference stayed the same.
    }, [
      currentSpeakerSignature,
      transcriptElements,
      videoCreator.isLoading,
      activeTemplateSettings?.activeVideoTemplateId,
      activeTemplate?.configuration?.namecardConfiguration?.enabled,
      isApplyingTemplate,
    ]);

    // Track which video we've loaded settings for to avoid duplicate loads
    const loadedSettingsForVideoRef = useRef<string | null>(null);

    // Pending auto-save: populates a newly created template's config, since
    // createVideoTemplate leaves an empty record. Armed only by
    // saveTemplateAsCopy, which is defined before updateTemplateConfig.
    const pendingAutoSaveRef = useRef<Array<{
      type: VideoTemplateElementType;
    }> | null>(null);

    // Guard: prevents the settings load useEffect from clearing activeTemplateSettings
    // immediately after a new template is created (before the auto-save useEffect runs).
    const preserveSettingsRef = useRef(false);

    // Initialize active template settings when currentVideo becomes available
    // Since this component is wrapped with observer(), it re-renders when MobX observables change
    // We use a ref to ensure we only load settings once per video
    useEffect(() => {
      // Skip if not ready
      if (!videoCreator.isInitialized) {
        // This condition will prevent settings from briefly disappearing while the new aspect ratio is getting created.
        // Also preserve settings if a template re-apply is pending (AR change in progress)
        // or if an auto-save of template config is pending.
        if (
          !videoCreator.isLoading &&
          !videoCreator.isSaving &&
          !videoCreator.pendingTemplateReapplyId &&
          !preserveSettingsRef.current
        ) {
          setActiveTemplateSettings(undefined);
        }
        return;
      }

      // Skip if already loaded for this video
      if (loadedSettingsForVideoRef.current === currentVideoId) {
        return;
      }

      // Mark as loaded for this video
      loadedSettingsForVideoRef.current = currentVideoId;

      const settings = getElementMetaObject(
        videoCreator.currentVideo,
        VIDEO_TEMPLATE_SETTINGS_KEY,
      ) as VideoTemplateSettings | undefined;

      if (settings) {
        setActiveTemplateSettings(
          settings as VideoTemplateSettings | undefined,
        );
        if (isClipMode) {
          setCurrentTab(VideoTemplateTab.SETTINGS);
        }
      } else {
        setActiveTemplateSettings(undefined);
        setCurrentTab(VideoTemplateTab.GRID);
      }
    }, [
      videoCreator.isInitialized,
      videoCreator.isLoading,
      videoCreator.isSaving,
      currentVideoId,
    ]);

    // Auto-switch to settings tab when a template is already applied
    useEffect(() => {
      // This condition will prevent tab from briefly switching to grid while the new aspect ratio is getting created
      if (
        !videoCreator.isInitialized ||
        videoCreator.isSaving ||
        videoCreator.isLoading
      ) {
        return;
      }
      if (activeTemplateSettings && activeTemplate) {
        setCurrentTab(VideoTemplateTab.SETTINGS);
      } else {
        setCurrentTab(VideoTemplateTab.GRID);
      }
    }, [
      activeTemplateSettings,
      activeTemplate,
      videoCreator.isInitialized,
      videoCreator.isSaving,
      videoCreator.isLoading,
    ]);

    // Fetch video templates
    useEffect(() => {
      // In clip mode, always fetch templates (bypass feature flags)
      // In normal mode, check feature flags
      if (!isClipMode) {
        if (
          !creatorStudioEnableDesignTabs ||
          !creatorStudioEnableVideoTemplatesTab ||
          !videoCreator.organization
        )
          return;
      } else {
        // In clip mode, just need organization
        if (!videoCreator.organization) return;
      }

      const fetchVideoTemplates = async () => {
        setIsLoading(true);
        const videoTemplates =
          await datoClientStore.videoTemplateRepository.getVideoTemplates(
            videoCreator.rootStore.templatedPromptsStore.videoTemplatesIds,
            {
              excludeGlobalPresets:
                videoCreator.organization?.customerType
                  ?.excludeFromGlobalTemplate ?? false,
            },
          );
        setAllVideoTemplates(videoTemplates);
        setIsLoading(false);
      };
      fetchVideoTemplates();
    }, [
      datoClientStore,
      creatorStudioEnableDesignTabs,
      creatorStudioEnableVideoTemplatesTab,
      videoCreator.organization,
      videoCreator.rootStore.templatedPromptsStore.videoTemplatesIds,
      isClipMode,
    ]);

    // Reset to grid tab when leaving video template menu (not applicable in clip mode)
    useEffect(() => {
      if (isClipMode) return; // Skip in clip mode - no design menu
      if (videoCreator.designSubMenu !== DesignCard.videoTemplate) {
        setCurrentTab(VideoTemplateTab.GRID);
      }
    }, [videoCreator.designSubMenu, isClipMode]);

    // Editor change observer for element/track removal
    const handleEditorChange = useCallback(
      (editor: VideoEditor, changeType: EditorChangeType, payload: any) => {
        if (changeType === EditorChangeType.ELEMENT_REMOVED) {
          const elementId = payload.elementId;
          updateTemplateSettingsWithFilter(e => e.id !== elementId);
        } else if (changeType === EditorChangeType.TRACK_REMOVED) {
          // Read the id from `trackId` — the one field every sender of this
          // notification supplies. Reaching through `payload.track` threw here,
          // and an observer's throw travels back out through the command runner
          // and costs the caller its save.
          const trackId: string | undefined = payload.trackId;
          if (trackId === undefined) return;

          if (trackId.toString() === KARAOKE_TRACK_NUMBER.toString()) {
            updateTemplateSettingsWithFilter(e => e.type !== 'captions');
          } else {
            const track = payload.track as Track | undefined;
            const elementIds = (track?.elements ?? []).map(e => e.id);
            updateTemplateSettingsWithFilter(e => !elementIds.includes(e.id));
          }
        } else if (
          changeType === EditorChangeType.EXTERNAL_UNDO ||
          changeType === EditorChangeType.EXTERNAL_REDO
        ) {
          // Snapshot-based undo/redo restore the project wholesale rather than
          // replaying ELEMENT_ADDED/REMOVED, so the incremental mirror goes
          // stale (e.g. a deleted nameCard's toggle stays off after undo). The
          // restored extraElementData is the source of truth — re-hydrate the
          // settings cache from it. These events fire only on undo/redo, so no
          // guarding against load/apply transitions is needed.
          const restored = getElementMetaObject(
            videoCreator.currentVideo,
            VIDEO_TEMPLATE_SETTINGS_KEY,
          ) as VideoTemplateSettings | undefined;
          setActiveTemplateSettings(restored);
        }
      },
      [updateTemplateSettingsWithFilter, videoCreator],
    );

    useEffect(() => {
      if (!editorStore.editor) return;

      const observer = {
        onEditorChange: handleEditorChange,
      };

      editorStore.editor.registerObserver(observer);
      return () => {
        editorStore.editor.unregisterObserver(observer);
      };
    }, [editorStore.editor, handleEditorChange]);

    // ============================================================================
    // UPDATE TEMPLATE CONFIG - Persist enabled states and placements to Dato CMS
    // ============================================================================

    /**
     * Builds the read-only template/editor context objects consumed by the
     * `collect*Placement` helpers. Shared by `buildConfigsFromResult` and
     * `getPlacementDeviation` so both read from one source of truth.
     */
    const buildTemplateContexts = useCallback((): {
      templateCtx: TemplateConfigContext;
      editorCtx: EditorElementContext;
    } | null => {
      if (!activeTemplate || !activeTemplateSettings) return null;

      const templateCtx: TemplateConfigContext = {
        activeTemplate,
        activeTemplateSettings,
        getCompositionById,
      };

      const editorCtx: EditorElementContext = {
        getElementById: (id: string) => editorStore.editor?.getElementById(id),
        karaokeLines: karaokeStore.karaokeLines,
        defaultKaraokeConfig: karaokeStore.defaultKaraokeConfig,
        getKaraokeConfigForKaraokeClipId,
        rendererGetElements: () => videoCreator.renderer?.getElements() ?? [],
        currentTime: videoCreator.time,
        videoSourceWidth:
          videoCreator.rootStore?.videoSourceStore?.source?.width || 1280,
        videoSourceHeight:
          videoCreator.rootStore?.videoSourceStore?.source?.height || 720,
        resolveKaraokeBrandingRecord: (id: string) =>
          videoCreator.textBrandingService.getKaraokeBrandingRecord(id) ??
          // A karaoke record referenced only by a video template is in neither
          // showcase list, and the replace modal offers it (the effect above
          // feeds those into `brandingTemplates`). The template's own copy is
          // the record as DatoCMS returned it.
          videoTemplatesConfigured
            .map(t => t.configuration?.captionsConfiguration?.captionsConfig)
            .find(config => config?.id === id) ??
          null,
        currentAspectRatio,
      };

      return { templateCtx, editorCtx };
    }, [
      activeTemplate,
      activeTemplateSettings,
      editorStore,
      currentAspectRatio,
      videoCreator,
      getCompositionById,
      getKaraokeConfigForKaraokeClipId,
      karaokeStore,
      videoTemplatesConfigured,
    ]);

    /**
     * Whether the live editor placement for `type` deviates from what the
     * template already stores — i.e. whether applying it would actually change
     * the template. Drives the placement toggle default in the update modal.
     */
    const getPlacementDeviation = useCallback(
      (type: VideoTemplateElementType): boolean => {
        if (!activeTemplate) return false;
        const contexts = buildTemplateContexts();
        if (!contexts) return false;

        const scratch: ConfigAccumulators = {
          repoConfigs: {},
          localOverrides: {},
        };
        const enabledChanges: Partial<
          Record<VideoTemplateElementType, boolean>
        > = {};
        const input = {
          ...contexts,
          enabledChanges,
        };

        if (type === 'logo') {
          if (collectLogoPlacement(input, scratch) === 'skip') return false;
        } else if (type === 'captions') {
          if (collectCaptionsPlacement(input, scratch) === 'skip') return false;
        } else if (type === 'nameCard') {
          if (collectNamecardPlacement(input, scratch) === 'skip') return false;
        } else if (type === 'hook') {
          if (collectHookPlacement(input, scratch) === 'skip') return false;
        } else {
          return false;
        }

        return placementDiffersFromStored(
          type,
          scratch.localOverrides,
          activeTemplate.configuration,
        );
      },
      [activeTemplate, buildTemplateContexts],
    );

    /**
     * Builds repo-formatted configs and local overrides from the modal result.
     * Shared by updateTemplateConfig and saveTemplateAsCopy.
     */
    const buildConfigsFromResult = useCallback(
      ({
        enabledChanges,
        placementTypes,
        replacementTypes,
        lockPositionChanges,
      }: UpdateTemplatePlacementResult): RepoConfigsResult | null => {
        if (!activeTemplate || !activeTemplateSettings) return null;

        const contexts = buildTemplateContexts();
        if (!contexts) return null;
        const { templateCtx, editorCtx } = contexts;

        const configAccumulators: ConfigAccumulators = {
          repoConfigs: {},
          localOverrides: {},
        };

        const placementInput = { templateCtx, editorCtx, enabledChanges };

        // --- Gather placement configs ---
        for (const elementType of placementTypes) {
          if (elementType === 'logo') {
            if (
              collectLogoPlacement(placementInput, configAccumulators) ===
              'skip'
            )
              continue;
          }
          if (elementType === 'captions') {
            if (
              collectCaptionsPlacement(placementInput, configAccumulators) ===
              'skip'
            )
              continue;
          }
          if (elementType === 'nameCard') {
            if (
              collectNamecardPlacement(placementInput, configAccumulators) ===
              'skip'
            )
              continue;
          }
          if (elementType === 'hook') {
            if (
              collectHookPlacement(placementInput, configAccumulators) ===
              'skip'
            )
              continue;
          }
        }

        // --- Gather enabled-only changes (for types not already handled by placement) ---
        const handledByPlacement = new Set(placementTypes);
        for (const [typeStr, enabled] of Object.entries(enabledChanges)) {
          const elementType = typeStr as VideoTemplateElementType;
          if (handledByPlacement.has(elementType)) continue;
          applyEnabledOnlyChange(
            elementType,
            enabled,
            activeTemplate.configuration,
            configAccumulators,
          );
        }

        // --- Gather replacement changes (merge into existing configs if already built) ---
        for (const elementType of replacementTypes) {
          const replacedId =
            activeTemplateSettings.replacedElementIds?.[elementType];
          if (!replacedId) continue;

          const replacementInput = { templateCtx, editorCtx, replacedId };

          if (
            elementType === 'introCard' ||
            elementType === 'endCard' ||
            elementType === 'nameCard' ||
            elementType === 'hook'
          ) {
            mergeCardReplacement(
              elementType,
              replacementInput,
              configAccumulators,
            );
          }

          if (elementType === 'captions') {
            mergeCaptionsReplacement(replacementInput, configAccumulators);
          }

          if (elementType === 'logo') {
            if (
              mergeLogoReplacement(replacementInput, configAccumulators) ===
              'skip'
            )
              continue;
          }
        }

        // --- Apply lock-position changes on top of any config built above ---
        // For a type already accumulated (placement/enabled/replacement) just
        // flip its `lockPosition`; otherwise seed a fresh config for a pure
        // lock toggle.
        const configKeyByType: Record<
          VideoTemplateElementType,
          keyof RepoConfigsResult['repoConfigs']
        > = {
          logo: 'logoConfiguration',
          captions: 'captionsConfiguration',
          nameCard: 'namecardConfiguration',
          introCard: 'introCardConfiguration',
          endCard: 'endCardConfiguration',
          hook: 'hookConfiguration',
        };
        for (const [typeStr, lockPosition] of Object.entries(
          lockPositionChanges ?? {},
        )) {
          const elementType = typeStr as VideoTemplateElementType;
          const configKey = configKeyByType[elementType];
          const repoBuilt = configAccumulators.repoConfigs[configKey];
          const localBuilt = configAccumulators.localOverrides[configKey];
          if (repoBuilt && localBuilt) {
            (repoBuilt as Record<string, unknown>).lockPosition = lockPosition;
            (localBuilt as Record<string, unknown>).lockPosition = lockPosition;
          } else {
            applyLockPositionChange(
              elementType,
              lockPosition,
              activeTemplate.configuration,
              configAccumulators,
            );
          }
        }

        return configAccumulators;
      },
      [
        activeTemplate,
        activeTemplateSettings,
        editorStore,
        currentAspectRatio,
        videoCreator,
        getCompositionById,
      ],
    );

    const updateTemplateConfig = useCallback(
      async (result: UpdateTemplatePlacementResult) => {
        if (!activeTemplate || !activeTemplateSettings) return;

        if (!hasAccessToEditAll && !hasAccessToEditThisTemplate) {
          logger.warn('User does not have access to edit template');
          return;
        }

        const fullActiveTemplate = allVideoTemplates.find(
          t => t.id === activeTemplate.id,
        );
        if (!fullActiveTemplate) {
          logger.warn('Template not found for config update', {
            templateId: activeTemplate.id,
          });
          return;
        }

        const built = buildConfigsFromResult(result);
        if (!built) return;
        const { repoConfigs, localOverrides } = built;

        if (Object.keys(repoConfigs).length === 0) return;

        const overrideKey =
          getCurrentOverrideConfigurationKey(currentAspectRatio);

        try {
          await datoClientStore.videoTemplateRepository.updateConfigurations(
            activeTemplate.id,
            overrideKey,
            repoConfigs,
          );

          const emptyConfig = {
            logoConfiguration: null,
            introCardConfiguration: null,
            endCardConfiguration: null,
            captionsConfiguration: null,
            namecardConfiguration: null,
            hookConfiguration: null,
          };

          const existingOverride = fullActiveTemplate[overrideKey];
          const updatedOverride: VideoTemplateConfig = {
            ...(existingOverride ?? emptyConfig),
            ...localOverrides,
          };
          setAllVideoTemplates(prev =>
            prev.map(t =>
              t.id === activeTemplate.id
                ? { ...t, [overrideKey]: updatedOverride }
                : t,
            ),
          );

          // Clear replacedElementIds for types that were persisted to DatoCMS
          if (result.replacementTypes.length > 0 && activeTemplateSettings) {
            const updatedReplacedIds = {
              ...(activeTemplateSettings.replacedElementIds || {}),
            };
            for (const type of result.replacementTypes) {
              delete updatedReplacedIds[type];
            }
            const updatedSettings: VideoTemplateSettings = {
              ...activeTemplateSettings,
              replacedElementIds: updatedReplacedIds,
              ...(result.replacementTypes.includes('logo')
                ? { replacedLogoUploadId: undefined }
                : {}),
            };
            upsertElementMetaObject(
              videoCreator.currentVideo,
              VIDEO_TEMPLATE_SETTINGS_KEY,
              updatedSettings,
            );
            setActiveTemplateSettings(updatedSettings);
          }

          logger.info('Template config updated in Dato CMS', {
            templateId: activeTemplate.id,
            aspectRatio: currentAspectRatio,
            ...result,
          });

          await saveIfCurrentVideoLatest();
        } catch (error) {
          logger.error('Failed to update template config', {
            error: extractErrorInfo(error),
            ...result,
          });
        }
      },
      [
        activeTemplate,
        activeTemplateSettings,
        allVideoTemplates,
        buildConfigsFromResult,
        datoClientStore,
        currentAspectRatio,
        videoCreator,
        hasAccessToEditAll,
        hasAccessToEditThisTemplate,
        saveIfCurrentVideoLatest,
      ],
    );

    // Process pending auto-save for a newly created template. Arm the ref in the
    // same synchronous block as a setState — never across an `await`, or the ref
    // write schedules no render, no dep below changes, and the save sits queued
    // until something unrelated re-identifies updateTemplateConfig.
    useEffect(() => {
      const elements = pendingAutoSaveRef.current;
      if (!elements) return;
      if (!activeTemplate || !activeTemplateSettings) return;
      if (!hasAccessToEditAll && !hasAccessToEditThisTemplate) return;

      pendingAutoSaveRef.current = null;
      preserveSettingsRef.current = false;

      const placementTypes = elements.map(e => e.type);
      const enabledChanges: Partial<Record<VideoTemplateElementType, boolean>> =
        {};
      for (const el of elements) {
        enabledChanges[el.type] = true;
      }

      updateTemplateConfig({
        enabledChanges,
        placementTypes,
        replacementTypes: [],
        lockPositionChanges: {},
      }).catch(err => {
        logger.warn(
          'Non-fatal: failed to auto-save template config after apply',
          { err },
        );
      });
    }, [
      activeTemplate,
      activeTemplateSettings,
      updateTemplateConfig,
      hasAccessToEditAll,
      hasAccessToEditThisTemplate,
    ]);

    const saveTemplateAsCopy = useCallback(
      async (result: UpdateTemplatePlacementResult, label: string) => {
        if (!activeTemplate || !activeTemplateSettings) return;

        const organization = videoCreator.organization;
        const customerType = organization?.customerType;
        if (!customerType?.id) {
          logger.error(
            'Cannot save template copy: no customer type found on showcase',
            { showcaseId: organization?.id, showcaseSlug: organization?.slug },
          );
          videoCreator.showToast({
            state: 'error',
            message:
              'This showcase has no Customer Type set in DatoCMS, so templates cannot be saved to it.',
          });
          return;
        }

        const built = buildConfigsFromResult(result);
        if (!built) return;
        const { repoConfigs } = built;

        try {
          const newTemplateId =
            await datoClientStore.videoTemplateRepository.duplicateVideoTemplate(
              label,
              currentAspectRatio,
              repoConfigs,
              {
                parentTemplateId: activeTemplate.id,
                parentShowcaseId: organization?.id ?? '',
                createdByUserId: datoPerson.person?.id ?? '',
                showcaseName: organization?.title ?? '',
              },
            );

          // Add to customer type's videoTemplates
          const linkedIds =
            await datoClientStore.videoTemplateRepository.addTemplateToCustomerType(
              customerType.id,
              newTemplateId,
            );

          // Refresh the template list
          const updatedTemplates =
            await datoClientStore.videoTemplateRepository.getVideoTemplates(
              linkedIds,
              {
                excludeGlobalPresets:
                  videoCreator.organization?.customerType
                    ?.excludeFromGlobalTemplate ?? false,
              },
            );

          setAllVideoTemplates(updatedTemplates);
          const newTemplateSettings: VideoTemplateSettings = {
            ...activeTemplateSettings,
            activeVideoTemplateId: newTemplateId,
            replacedElementIds: {},
            replacedLogoUploadId: undefined,
          };

          setActiveTemplateSettings(newTemplateSettings);

          upsertElementMetaObject(
            videoCreator.currentVideo,
            VIDEO_TEMPLATE_SETTINGS_KEY,
            newTemplateSettings,
          );

          logger.info('Template saved as copy', {
            sourceTemplateId: activeTemplate.id,
            newTemplateId,
            label,
          });
        } catch (error) {
          logger.error('Failed to save template as copy', { error, label });
          videoCreator.showToast({
            state: 'error',
            message: 'Saving the template as a copy failed. Please try again.',
          });
        }
      },
      [
        activeTemplate,
        activeTemplateSettings,
        buildConfigsFromResult,
        currentAspectRatio,
        datoClientStore,
        hasAccessToEditAll,
        userIdentity,
        datoPerson,
        videoCreator.organization,
        hasAccessToEditThisTemplate,
      ],
    );

    const createNewTemplate = useCallback(async () => {
      const label = DRAFT_TEMPLATE_LABEL;
      const organization = videoCreator.organization;
      const customerType = organization?.customerType;

      if (!customerType?.id) {
        // The grid can list templates without this — they resolve through
        // TemplatedPromptsRepository's fallback customer type when the showcase
        // has none. Creating can't: the new template has to be linked to the
        // showcase's own customer type. So log which half is missing.
        logger.error(
          'Cannot create template: no customer type found on showcase',
          {
            hasOrganization: !!organization,
            showcaseId: organization?.id,
            showcaseSlug: organization?.slug,
            showcaseTitle: organization?.title,
            storyId: videoCreator.story?.id,
          },
        );
        videoCreator.showToast({
          state: 'error',
          message: organization
            ? 'This showcase has no Customer Type set in DatoCMS, so templates cannot be created for it.'
            : 'No showcase is loaded, so a template cannot be created yet.',
        });
        return;
      }

      if (!hasAccessToEditAll) {
        return;
      }

      // Need at least one existing template to discover block model IDs.
      // When global presets are excluded and the customer has no templates
      // yet, fetch a global preset solely as a structural reference.
      let referenceTemplateId: string | undefined;
      if (allVideoTemplates.length > 0) {
        referenceTemplateId = allVideoTemplates[0].id;
      } else {
        const globalId =
          await datoClientStore.videoTemplateRepository.getReferenceGlobalPresetId();
        if (!globalId) {
          logger.error(
            'Cannot create template: no reference template available',
            {
              showcaseId: organization?.id,
              storyId: videoCreator.story?.id,
            },
          );
          videoCreator.showToast({
            state: 'error',
            message: 'Creating the template failed. Please try again.',
          });
          return;
        }
        referenceTemplateId = globalId;
      }

      try {
        // 1. Create empty template in DatoCMS
        const newTemplateId =
          await datoClientStore.videoTemplateRepository.createVideoTemplate(
            label,
            {
              parentTemplateId: referenceTemplateId,
              parentShowcaseId: organization?.id ?? '',
              createdByUserId: datoPerson.person?.id ?? '',
              showcaseName: organization?.title ?? '',
            },
          );

        // 2. Add to customer type's videoTemplates
        const linkedIds =
          await datoClientStore.videoTemplateRepository.addTemplateToCustomerType(
            customerType.id,
            newTemplateId,
          );

        // 3. Refresh the template list
        const updatedTemplates =
          await datoClientStore.videoTemplateRepository.getVideoTemplates(
            linkedIds,
            {
              excludeGlobalPresets:
                videoCreator.organization?.customerType
                  ?.excludeFromGlobalTemplate ?? false,
            },
          );
        setAllVideoTemplates(updatedTemplates);

        // Set the new template as active and navigate to settings tab.
        // Carry over existing template elements from the editor so the
        // new template inherits the current logo, captions, cards, etc.
        const existingElements =
          activeTemplateSettings?.videoTemplateElements ?? [];
        const newTemplateSettings: VideoTemplateSettings = {
          activeVideoTemplateId: newTemplateId,
          videoTemplateElements: existingElements,
          replacedElementIds: {},
        };
        setActiveTemplateSettings(newTemplateSettings);

        // Trigger auto-save of element configs to the new template's
        // DatoCMS record so it isn't left empty.
        if (existingElements.length > 0) {
          pendingAutoSaveRef.current = existingElements;
          preserveSettingsRef.current = true;
        }
        upsertElementMetaObject(
          videoCreator.currentVideo,
          VIDEO_TEMPLATE_SETTINGS_KEY,
          newTemplateSettings,
        );
        setCurrentTab(VideoTemplateTab.SETTINGS);

        logger.info('New template created and linked to customer type', {
          templateId: newTemplateId,
          customerTypeId: customerType.id,
          label,
          updatedTemplateCount: updatedTemplates.length,
        });
      } catch (error) {
        logger.error('Failed to create new template', {
          error: extractErrorInfo(error),
          label,
        });
        videoCreator.showToast({
          state: 'error',
          message: 'Creating the template failed. Please try again.',
        });
      }
    }, [
      allVideoTemplates,
      videoCreator.organization,
      datoClientStore,
      hasAccessToEditAll,
      datoPerson,
      hasAccessToEditThisTemplate,
      activeTemplateSettings,
      userIdentity,
    ]);

    const renameTemplate = useCallback(
      async (templateId: string, label: string) => {
        const trimmed = label.trim();
        if (!trimmed) return;
        if (!hasAccessToEditAll && !hasAccessToEditThisTemplate) return;

        const template = allVideoTemplates.find(t => t.id === templateId);
        if (!template || template.label === trimmed) return;

        // Global presets don't follow the customer internal-name shape, so only
        // customer templates get their internal name kept in sync.
        const internalName = template.isGlobalPreset
          ? undefined
          : `Customer | ${trimmed} | ${videoCreator.organization?.title ?? ''}`;

        try {
          await datoClientStore.videoTemplateRepository.renameVideoTemplate(
            templateId,
            trimmed,
            internalName,
          );

          setAllVideoTemplates(prev =>
            prev.map(t =>
              t.id === templateId
                ? {
                    ...t,
                    label: trimmed,
                    internalName: internalName ?? t.internalName,
                  }
                : t,
            ),
          );

          logger.info('Renamed video template', {
            templateId,
            label: trimmed,
          });
        } catch (error) {
          logger.error('Failed to rename video template', {
            error: extractErrorInfo(error),
            templateId,
            label: trimmed,
          });
          videoCreator.showToast({
            state: 'error',
            message: 'Renaming the template failed. Please try again.',
          });
        }
      },
      [
        allVideoTemplates,
        datoClientStore,
        hasAccessToEditAll,
        hasAccessToEditThisTemplate,
        videoCreator.organization,
      ],
    );

    const value: VideoTemplatesContextType = {
      videoTemplates: videoTemplatesConfigured,
      isLoading: isLoading,
      addVideoTemplate: handleAddVideoTemplate,
      clearTemplate: clearTemplate,
      activeTemplate: activeTemplate,
      activeTemplateSettings: activeTemplateSettings,
      setActiveTemplateSettings: setActiveTemplateSettings,
      currentTab: currentTab,
      setCurrentTab: setCurrentTab,
      toggleTemplateElement: toggleTemplateElement,
      saveIfCurrentVideoLatest: saveIfCurrentVideoLatest,
      replaceTemplateElement: replaceTemplateElement,
      getReplacedElementId: getReplacedElementId,
      syncKaraokeBrandingTemplates,
      speakers: speakers,
      videoCreator,
      editor: editorStore.editor,
      checkAndAddNamecardsForNewSpeakers,
      isClipMode,
      updateTemplateConfig,
      getPlacementDeviation,
      hasAccessToEditAll,
      hasAccessToEditThisTemplate,
      createNewTemplate,
      renameTemplate,
      saveTemplateAsCopy,
    };

    return (
      <VideoTemplatesContext.Provider value={value}>
        {children}
      </VideoTemplatesContext.Provider>
    );
  });
